import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import '../styles/FAQSection.css';

const FAQSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [openQuestion, setOpenQuestion] = useState(null);

  const toggleQuestion = (index) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  const faqData = [
    {
      question: "¿Qué cuentas de Instagram / Facebook puedo automatizar?",
      answer: "Solo las cuentas de tipo empresa y/o creador pueden ser vinculadas con nuestro servicio."
    },
    {
      question: "¿Qué incluye el acceso a la plataforma?",
      answer: "Nuestra plataforma ofrece: Analíticas, CRM, Bandeja de entrada unificada de todas las plataformas conectadas, Usuarios ilimitados con distinto nivel de acceso, Seguimiento de mensajes, Mensajería masiva para los canales conectados, Creador visual de flujos de trabajo, Sección de contactos para filtrar / exportar / importar, Manejo de palabras claves, Herramientas para automatización de comentarios en posts de Facebook e Instagram, Generador de códigos QR, Herramienta de gestión de calendarios, Herramienta E-Commerce para trabajar con catálogos de productos / servicios, Herramienta para gestionar ubicaciones, Webhooks y API requests para conectar con otros sistemas, Herramienta de simulación del bot para mostrar a clientes, Modo desarrollo para hacer pruebas sin impactar a sus clientes. Entre otras funcionalidades."
    },
    {
      question: "¿Cuánto tardo en automatizar mis canales de ventas?",
      answer: "Eso depende de la estrategia que quieras lograr, por lo general conectar las cuentas y escribir un prompt básico puede llevar desde 5 a 15 minutos dependiendo la cantidad de cuentas. Puede ser que implementar estrategias avanzadas lleve más tiempo, nuestra interfaz es muy amigable para los nuevos usuarios."
    },
    {
      question: "¿Qué incluye el Plan Full?",
      answer: "El Plan Full incluye asesoría completa de estrategia comercial, análisis y mejora de procesos, y la configuración 100% personalizada por nuestro equipo. Nos comprometemos a analizar tu estrategia actual, proponer soluciones tecnológicas, configurar todas las automatizaciones y conectarlas a tus canales reales.\n\nIncluye todas las herramientas del Plan Pro más:\n\n– Asesoría completa de estrategia comercial.\n– Análisis y mejora de procesos comerciales.\n– Grupo de WhatsApp dedicado con nuestro equipo.\n– Respuestas de preguntas frecuentes automatizadas.\n– Filtro de tráfico de clientes y derivación hacia canales de ventas.\n– Catálogos de productos e IA para responder consultas.\n– Tipo de respuestas: Texto, Audio e Imágenes.\n– Refinamiento continuo y actualizaciones a pedido.\n\nNuestro equipo conecta todos los canales reales del cliente (WhatsApp, Instagram, TikTok, Telegram, etc)."
    },
    {
      question: "¿Qué diferencia hay entre el Plan Pro y el Plan Full?",
      answer: "El Plan Pro da acceso completo a todas las herramientas de la plataforma con IA incluida (OpenAI GPT-4o). El cliente gestiona y configura sus propias automatizaciones en modalidad auto gestión.\n\nEl Plan Full incluye todo el Plan Pro más asesoría completa de estrategia comercial, análisis de procesos, y la configuración 100% personalizada por nuestro equipo experto. Ideal para empresas que quieren delegar completamente la configuración y obtener orientación estratégica."
    },
    {
      question: "¿Precios en pesos Argentinos?",
      answer: "Ofrecemos precios en pesos Argentinos, pero es importante saber que toda la infraestructura detrás de nuestro sistema consume servicios del exterior y por ellos nuestros precios serán actualizados."
    },
    {
      question: "¿Puedo crear chatbots para mis clientes?",
      answer: "Claro que si, en caso de que este sea tu objetivo te enviaremos tutoriales para poder conectar las cuentas de tus clientes y comenzar a vender chatbots."
    },
    {
      question: "¿Qué son los tokens de OpenAI y para que sirven?",
      answer: "Los tokens de Open AI son la unidad que se utiliza para medir el consumo de recursos de la AI. El consumo de tokens por respuesta va a variar dependiendo la configuración del bot y el tipo de herramientas utilizadas en la configuración de la cuenta. La generación de texto es el mecanismo que menor cantidad de tokens consume, la generación de audio e imágenes consume mayor cantidad de tokens.\n\nUn ejemplo general:\n\"Hola! como te va? como te puedo ayudar hoy? Recuerda que tenemos promociones especiales por el día de la madre!\"\nEsta respuesta consume 25 tokens. Los tokens son contados cuando el usuario envía mensajes y cuando la AI responde.\n\nImportante: Los planes Pro y Full incluyen 3 millones de tokens de OpenAI que se renuevan mensualmente.\n\nRunamatic ofrece herramientas como botones, respuestas automatizadas por palabras claves que no tienen consumo alguno en recursos de la AI."
    },
    {
      question: "¿Trabajan con mi número de WhatsApp?",
      answer: "Si, podemos integrar tu cuenta existente de WhatsApp Business y si no tienes podemos crear una nueva cuenta de WhatsApp Cloud API. Podemos trabajar de las dos maneras. Esto te ofrece poder seguir viendo los mensajes en tu teléfono y al mismo tiempo usar las automatizaciones."
    },
    {
      question: "¿Tenés más preguntas?",
      answer: "Escribinos por WhatsApp con el link que encontrarás más abajo, también podés escribirnos a hola@runamatic.io.\nEncontrá a nuestro equipo de soporte por el chat interno de nuestra plataforma, simplemente pedile a la AI que te comunique con un humano."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="faq" className="faq-section section-page" ref={ref}>
      <div className="faq-background">
        <div className="faq-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
        </div>
      </div>

      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h2>Preguntas Frecuentes</h2>
          <p>
            Resolvemos las dudas más comunes sobre Runamatic y nuestros servicios de automatización con IA
          </p>
        </motion.div>

        <motion.div 
          className="faq-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <div className="faq-grid">
            {faqData.map((item, index) => (
              <motion.div
                key={index}
                className={`faq-item ${openQuestion === index ? 'active' : ''}`}
                variants={itemVariants}
              >
                <div 
                  className="faq-question"
                  onClick={() => toggleQuestion(index)}
                >
                  <h3>{item.question}</h3>
                  <div className={`faq-icon ${openQuestion === index ? 'rotated' : ''}`}>
                    <svg 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path 
                        d="M6 9L12 15L18 9" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
                
                <motion.div 
                  className="faq-answer"
                  initial={false}
                  animate={{
                    height: openQuestion === index ? "auto" : 0,
                    opacity: openQuestion === index ? 1 : 0
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut"
                  }}
                >
                  <div className="faq-answer-content">
                    {item.answer.split('\n').map((paragraph, pIndex) => (
                      paragraph.trim() && (
                        <p key={pIndex}>{paragraph}</p>
                      )
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>


        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection; 